import { extractClassGroupNamesFromEvent } from '@bp/shared-planung-utils';
import {
  Button,
  Grid,
  GridColumn,
  GridRow,
  Pagination,
  showToast,
  Table,
  TableColumns,
  Tooltip,
  useDefaultSelecting,
} from '@bp/ui-components';
import { SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useConfirm } from '../../hooks/useConfirm';
import { useLoadBasicData } from '../../hooks/useLoadBasicData';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../hooks/useUserConfigContext';
import {
  EventJobStatus,
  SortDirection,
  use_CancelSynchronisationEventMutation,
  use_EventsCountQuery,
  use_EventsQuery,
  useEventJobsQuery,
} from '../../types/planung-graphql-client-defs';
import { createEvents, deleteEvents } from '../../utils/eventsApi';

type EventJobTableType = {
  uuid: string;
  from: string;
  to: string;
  processed: number;
  status: EventJobStatus;
  created: string;
};

type AdminEventsTableType = {
  uuid: string;
  start: string;
  end: string;
  comment: string;
  rooms: string;
  teachers: string;
  classesOrGroups: string;
  originalClassesOrGroups: string;
  subject: string;
};

export const AdminEvents = observer(() => {
  const { pimAuthClaims } = useAuthClaims();
  const perms = usePermissionChecker();

  const pageSize = 15;
  const [eventPage, setEventPage] = useState(0);

  const userConfigContext = useUserConfigContext();

  const schoolYear = userConfigContext.selectedSchoolYear;

  const [{ data: _eventJobsData }, execute] = useEventJobsQuery({
    variables: {
      where: {
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      options: {
        sort: [{ createdAt: SortDirection.Desc }],
      },
    },
    pause: !schoolYear,
    requestPolicy: 'network-only',
  });
  const [, cancelEventJob] = use_CancelSynchronisationEventMutation();

  const hasRunningEventJobs = _eventJobsData?.eventJobs.some((job) => job.status === EventJobStatus.Running);

  async function dangerHandleStopSync(uuid: string) {
    if (perms?.isOmniAdmin()) {
      await cancelEventJob({ uuid: uuid, update: { status: EventJobStatus.Cancelled } });
    }
    execute();
  }

  const [{ data }, fetch] = use_EventsQuery({
    variables: {
      where: {
        start_LTE: schoolYear?.end,
        end_GTE: schoolYear?.start,
      },
      options: {
        sort: [{ start: SortDirection.Asc }],
        limit: pageSize + 1,
        offset: eventPage * pageSize,
      },
    },
    pause: !schoolYear,
    requestPolicy: 'network-only',
  });

  const [{ data: countData }] = use_EventsCountQuery({
    variables: {
      where: {
        start_LTE: schoolYear?.end,
        end_GTE: schoolYear?.start,
      },
    },
    pause: !data,
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (hasRunningEventJobs) {
      const intervalId = setInterval(() => {
        execute();
      }, 2000);

      return () => {
        clearInterval(intervalId);
        fetch();
      };
    }
  }, [execute, fetch, hasRunningEventJobs]);

  const eventTotalCount = countData?.eventsConnection.totalCount ?? 0;
  const pageCount = Math.ceil(eventTotalCount / pageSize);

  const { ConfirmationDialog, confirm } = useConfirm();

  const handleCreateEvents = async () => {
    await confirm({
      title: 'Create Events',
      message: 'Do you really want to create events?',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: async () => {
        if (schoolYear) {
          await createEvents({
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            schoolYearUuid: schoolYear?.uuid,
          });
          showToast('Events creation started. Please wait up to 20 minutes for the changes to take effect.', {
            autoClose: 1200000,
            type: 'success',
            showCloseButton: false,
            onClose: () => {
              fetch();
            },
          });
        }
        const intervalId = setInterval(() => {
          execute();
        }, 1000);

        setTimeout(() => {
          clearInterval(intervalId);
        }, 1200000);
      },
    });
  };

  const handleDeleteEvents = async () => {
    await confirm({
      title: 'Delete Events',
      message: 'Do you really want to delete all events?',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: async () => {
        if (schoolYear) {
          await deleteEvents({
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            schoolYearUuid: schoolYear?.uuid,
          });
          showToast('Events deletion started. Please wait up to 3 seconds for the changes to take effect.', {
            autoClose: 3000,
            type: 'success',
            onClose: () => {
              fetch();
            },
          });
        }
      },
    });
  };

  const { groupsData, teacherData, subjectData, classesData, roomsData, divisionsData } = useLoadBasicData({
    pause: !schoolYear,
  });

  const eventJobColumns: TableColumns<EventJobTableType>[] = [
    {
      id: 'created',
      accessorKey: 'created',
      header: 'Created',
      size: 200,
    },
    {
      id: 'from',
      header: 'From',
      accessorKey: 'from',
      size: 200,
    },
    {
      id: 'to',
      header: 'To',
      accessorKey: 'to',
      size: 200,
    },
    {
      id: 'processed',
      accessorKey: 'processed',
      header: 'Processed',
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
    },
    {
      id: 'uuid',
      accessorKey: 'uuid',
      header: 'UUID',
      cell: ({ cell }) => {
        return <Tooltip content={cell.getValue() as string}>{cell.getValue() as string}</Tooltip>;
      },
    },
  ];

  const eventJobData: EventJobTableType[] = useMemo(() => {
    return (
      _eventJobsData?.eventJobs.map((job) => {
        return {
          uuid: job.uuid,
          from: dayjs(job.from).format('DD.MM.YYYY HH:mm Z'),
          to: dayjs(job.to).format('DD.MM.YYYY HH:mm Z'),
          processed: job.processed ?? 0,
          status: job.status,
          created: dayjs(job.createdAt).format('DD.MM.YYYY HH:mm Z'),
        };
      }) ?? []
    );
  }, [_eventJobsData?.eventJobs]);

  const columns: TableColumns<AdminEventsTableType>[] = [
    {
      id: 'start',
      accessorFn: ({ start }) => dayjs(start).utc().format('DD.MM.YYYY HH:mm Z'),
      header: 'Start',
      size: 200,
    },
    {
      id: 'end',
      accessorKey: 'end',
      header: 'End',
      size: 200,
      accessorFn: ({ end }) => dayjs(end).utc().format('DD.MM.YYYY HH:mm Z'),
    },
    {
      id: 'subject',
      accessorKey: 'subject',
      header: 'Subject',
    },
    {
      id: 'teachers',
      accessorKey: 'teachers',
      header: 'Teachers',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'classes',
      accessorKey: 'classes',
      header: 'Classes',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'groups',
      accessorKey: 'groups',
      header: 'Groups',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'rooms',
      accessorKey: 'rooms',
      header: 'Rooms',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'comment',
      accessorKey: 'comment',
      header: 'Comment',
    },
    {
      id: 'uuid',
      accessorKey: 'uuid',
      header: 'UUID',
      size: 50,
      cell: ({ cell }) => {
        return <Tooltip content={cell.getValue() as string}>{cell.getValue() as string}</Tooltip>;
      },
    },
  ];

  const tableData: AdminEventsTableType[] = useMemo(() => {
    const events: AdminEventsTableType[] =
      data?.events.map((event) => {
        const subject = subjectData?.subjects.find((s) => s.uuid === event.subject.uuid);
        const teacherNames = event.personsConnection.edges.map(
          (edge) => teacherData?.people.find((t) => t.uuid === edge.node.uuid)?.displayName,
        );

        const rooms = event.rooms.map((room) => roomsData?.rooms.find((r) => r.uuid === room.uuid));

        const { groupClassNamesLong } = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: event.classesOrGroups,
          data: { classesData, groupsData, divisionsData },
        });

        const { groupClassNamesLong: originalGroupClassNamesLong } = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: event.originalClassesOrGroups,
          data: { classesData, groupsData, divisionsData },
        });

        return {
          uuid: event.uuid,
          start: event.start,
          end: event.end,
          comment: event.comment ?? '',
          rooms: rooms.map((room) => room?.name).join(', '),
          teachers: teacherNames.join(', '),
          classesOrGroups: groupClassNamesLong,
          originalClassesOrGroups: originalGroupClassNamesLong,
          subject: subject?.name ?? '',
        };
      }) ?? [];

    // group by start
    const groupedByStart = events.reduce(
      (acc, event) => {
        const start = event.start;
        if (!acc[start]) {
          acc[start] = [];
        }
        acc[start].push(event);
        return acc;
      },
      {} as Record<string, AdminEventsTableType[]>,
    );

    // sort by class
    const sortedEvents = Object.values(groupedByStart).map((group) =>
      group.sort((a, b) => {
        const aClass = a.classesOrGroups
          ? a.classesOrGroups
          : a.originalClassesOrGroups
            ? a.originalClassesOrGroups
            : '';
        const bClass = b.classesOrGroups
          ? b.classesOrGroups
          : b.originalClassesOrGroups
            ? b.originalClassesOrGroups
            : '';
        return aClass.localeCompare(bClass, undefined, { numeric: true, sensitivity: 'base' });
      }),
    );

    return sortedEvents.flat();
  }, [
    classesData,
    data?.events,
    divisionsData,
    groupsData,
    roomsData?.rooms,
    subjectData?.subjects,
    teacherData?.people,
  ]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  return (
    <Grid>
      <GridRow headline={'EventJobs'}>
        <GridColumn width={12}>
          <Table
            columns={eventJobColumns}
            data={eventJobData}
            showPagination={true}
            pageSize={5}
            lastCol={({ original }) =>
              perms?.isOmniAdmin() && (
                <>
                  <Button onClick={() => dangerHandleStopSync(original.uuid)}>Stop!</Button>
                </>
              )
            }
          />
        </GridColumn>
      </GridRow>
      <GridRow headline={'Events'}>
        <GridColumn width={12}>
          <Table
            loading={hasRunningEventJobs}
            isOnWhite={false}
            showSort={true}
            showActionBar={true}
            sorting={sorting}
            showSelect={true}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            onSortingChange={setSorting}
            actionBarSettings={{
              showSelectedRowsCount: true,
              extendedActionsRight: (
                <>
                  <Button onClick={handleCreateEvents} hierarchy={'primary'} disabled={hasRunningEventJobs}>
                    Create Events
                  </Button>
                  <Button onClick={handleDeleteEvents} hierarchy={'primary'} disabled={hasRunningEventJobs}>
                    Delete all Events
                  </Button>
                </>
              ),
            }}
            columns={columns}
            data={tableData}
          />
          <Pagination
            pageCount={pageCount}
            currentPage={eventPage}
            nextPage={() => {
              setEventPage(eventPage + 1);
            }}
            previousPage={() => {
              setEventPage(eventPage - 1);
            }}
            lastPage={() => {
              setEventPage(pageCount - 1);
            }}
            firstPage={() => {
              setEventPage(0);
            }}
            setPageIndex={(index) => {
              setEventPage(index);
            }}
            getCanPreviousPage={() => eventPage > 0}
            getCanNextPage={() => eventPage < pageCount - 1}
          />
        </GridColumn>
      </GridRow>
      <ConfirmationDialog />
    </Grid>
  );
});
